import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { EmbedShadowRoot } from '@infl-ts/ui-shadow-dom';
import { useToggle } from '@infl-ts/util-hooks';
import { mergeSxProps } from '@infl-ts/util-merge-sx-props';
import { Box, ClickAwayListener, Paper, Popper as MUIPopper } from '@mui/material';
import { constVoid } from 'fp-ts/function';
import { useRef } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var arrowStyles = {
  '&[data-popper-placement="bottom"] .arrow': {
    top: 0,
    left: 0,
    marginTop: '-0.9em',
    width: '3em',
    height: '1em',
    '&::before': {
      borderWidth: '0 1em 1em 1em',
      borderColor: function borderColor(t) {
        return "transparent transparent ".concat(t.palette.background.paper, " transparent");
      }
    }
  },
  '&[data-popper-placement="top"] .arrow': {
    bottom: 0,
    left: 0,
    marginBottom: '-0.9em',
    width: '3em',
    height: '1em',
    '&::before': {
      borderWidth: '1em 1em 0 1em',
      borderColor: function borderColor(t) {
        return "".concat(t.palette.background.paper, " transparent transparent transparent");
      }
    }
  },
  '&[data-popper-placement="right"] .arrow': {
    left: 0,
    marginLeft: '-0.9em',
    height: '3em',
    width: '1em',
    '&::before': {
      borderWidth: '1em 1em 1em 0',
      borderColor: function borderColor(t) {
        return "transparent ".concat(t.palette.background.paper, " transparent transparent");
      }
    }
  },
  '&[data-popper-placement="left"] .arrow': {
    right: 0,
    marginRight: '-0.9em',
    height: '3em',
    width: '1em',
    '&::before': {
      borderWidth: '1em 0 1em 1em',
      borderColor: function borderColor(t) {
        return "transparent transparent transparent ".concat(t.palette.background.paper);
      }
    }
  }
};
export var Popover = function Popover(_ref) {
  var content = _ref.content,
      children = _ref.children,
      _ref$noPadding = _ref.noPadding,
      noPadding = _ref$noPadding === void 0 ? false : _ref$noPadding,
      _ref$onClose = _ref.onClose,
      onClose = _ref$onClose === void 0 ? constVoid : _ref$onClose,
      _ref$onOpen = _ref.onOpen,
      onOpen = _ref$onOpen === void 0 ? constVoid : _ref$onOpen,
      _ref$preventFallback = _ref.preventFallback,
      preventFallback = _ref$preventFallback === void 0 ? false : _ref$preventFallback,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'bottom-start' : _ref$placement,
      _ref$keepMounted = _ref.keepMounted,
      keepMounted = _ref$keepMounted === void 0 ? true : _ref$keepMounted,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$noBoxShadow = _ref.noBoxShadow,
      noBoxShadow = _ref$noBoxShadow === void 0 ? false : _ref$noBoxShadow,
      _ref$preventBubbling = _ref.preventBubbling,
      preventBubbling = _ref$preventBubbling === void 0 ? false : _ref$preventBubbling,
      _ref$clickAwayMouseEv = _ref.clickAwayMouseEvent,
      clickAwayMouseEvent = _ref$clickAwayMouseEv === void 0 ? 'onClick' : _ref$clickAwayMouseEv,
      _ref$arrow = _ref.arrow,
      arrow = _ref$arrow === void 0 ? false : _ref$arrow,
      zIndex = _ref.zIndex,
      sx = _ref.sx,
      containerSx = _ref.containerSx,
      _ref$fallbackPlacemen = _ref.fallbackPlacements,
      fallbackPlacements = _ref$fallbackPlacemen === void 0 ? ['left', 'top', 'right', 'bottom'] : _ref$fallbackPlacemen,
      _ref$enablePortal = _ref.enablePortal,
      enablePortal = _ref$enablePortal === void 0 ? false : _ref$enablePortal;
  var anchorRef = useRef(null);
  var arrowRef = useRef(null);

  var _useToggle = useToggle(false),
      _useToggle2 = _slicedToArray(_useToggle, 2),
      isOpen = _useToggle2[0],
      _useToggle2$ = _useToggle2[1],
      close = _useToggle2$.off,
      toggle = _useToggle2$.toggle;

  return /*#__PURE__*/_jsx(ClickAwayListener, {
    mouseEvent: clickAwayMouseEvent,
    onClickAway: function onClickAway() {
      isOpen && onClose();
      close();
    },
    "data-element": "ClickAwayListener",
    "data-component": "Popover",
    "data-source-file": "index.tsx",
    children: /*#__PURE__*/_jsxs(Box, {
      component: "span",
      sx: {
        flexGrow: 1
      },
      "data-element": "Box",
      "data-component": "Popover",
      "data-source-file": "index.tsx",
      children: [/*#__PURE__*/_jsx(Box, {
        sx: mergeSxProps({
          cursor: disabled ? 'default' : 'pointer',
          width: 'fit-content'
        }, containerSx),
        onClick: function onClick(e) {
          if (!disabled) {
            isOpen ? onClose() : onOpen();
            toggle();
            preventBubbling && e.stopPropagation();
          }
        },
        component: "span",
        ref: anchorRef,
        "data-element": "Box",
        "data-component": "Popover",
        "data-source-file": "index.tsx",
        children: typeof children === 'function' ? children({
          handleClosePopover: close,
          isOpen: isOpen
        }) : children
      }), /*#__PURE__*/_jsxs(MUIPopper, {
        sx: mergeSxProps({
          zIndex: zIndex
        }, arrow ? arrowStyles : {}),
        open: isOpen,
        anchorEl: anchorRef === null || anchorRef === void 0 ? void 0 : anchorRef.current,
        keepMounted: keepMounted,
        disablePortal: !enablePortal,
        modifiers: [{
          name: 'flip',
          enabled: true,
          options: preventFallback ? {} : {
            fallbackPlacements: fallbackPlacements,
            padding: {
              top: 64
            }
          }
        }, {
          name: 'arrow',
          enabled: true,
          options: {
            element: arrowRef.current
          }
        }, {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altBoundary: true,
            tether: false,
            rootBoundary: anchorRef,
            padding: 8
          }
        }],
        placement: placement,
        "data-element": "MUIPopper",
        "data-source-file": "index.tsx",
        children: [arrow ? /*#__PURE__*/_jsx(Box, {
          ref: arrowRef,
          className: "arrow",
          component: "span",
          sx: {
            position: 'absolute',
            fontSize: 7,
            width: '3em',
            height: '3em',
            '&::before': {
              content: '" "',
              margin: 'auto',
              display: 'block',
              width: 0,
              height: 0,
              borderStyle: 'solid'
            }
          }
        }) : null, /*#__PURE__*/_jsx(EmbedShadowRoot, {
          "data-element": "EmbedShadowRoot",
          "data-source-file": "index.tsx",
          children: /*#__PURE__*/_jsx(Paper, {
            sx: mergeSxProps({
              p: noPadding ? 0 : 3,
              boxShadow: noBoxShadow ? 0 : 8
            }, sx),
            "data-element": "Paper",
            "data-source-file": "index.tsx",
            children: content(close, isOpen)
          })
        })]
      })]
    })
  });
};